import { z, ZodType } from 'zod';

type Listener<T extends ZodType<unknown>> = (payload: z.TypeOf<T>) => void;
type Unsubscribe = () => void;

interface EmitStatus {
  success: boolean;
  error: string | null;
  listenersRegistered: number;
  listenersExecuted: number;
}

/**
 * Event bus that allows registering listeners for specific events
 * and emitting events with associated payloads, with added Zod-based type safety.
 *
 * ```typescript
 * const userCreatedSchema = z.object({
 *   name: z.string(),
 *   age: z.number()
 * });
 *
 * const userDeletedSchema = z.object({
 *   id: z.string()
 * });
 * const eventBus = new EventBus();
 *
 * eventBus.on('user.created', userCreatedSchema, payload => console.log(`User created: ${payload.name}, ${payload.age}`));
 * eventBus.on('user.deleted', userDeletedSchema, payload => console.log(`User deleted: ${payload.id}`));
 *
 * const emitStatus = eventBus.emit('user.created', { name: 'Alex', age: 20 });
 * console.log(emitStatus); // { success: true, error: null, listenersExecuted: 1 }
 *
 * const emitStatus2 = eventBus.emit('user.created', { name: 'Alex', age: '20' });
 * console.log(emitStatus2); // { success: false, error: "Payload validation failed for [user.created]: Invalid input", listenersExecuted: 0 }
 * ```
 */
export default class EventBus {

  static unsubscribeAll(calls: Unsubscribe|Unsubscribe[]|null): void {
      if(Array.isArray(calls)) {
        calls.forEach(call => call());
      } else if(calls instanceof Function) {
        calls();
      }
  }

  private listeners: {
    [eventName: string]: Array<{
      schema: ZodType<unknown>;
      callback: Listener<ZodType<unknown>>
    }>
  } = {};


  /**
   * Emits an event with the given event name and payload to all registered listeners.
   *
   * @param {string} eventName - The name of the event.
   * @param {T} payload - The payload associated with the event.
   *
   * @returns {EmitStatus} - The status of the emit operation.
   */
  emit<T>(eventName: string, payload: T): EmitStatus {
      const listeners = this.listeners[eventName];

      if (!listeners) {
        return {
          success: true,
          error: null,
          listenersExecuted: 0,
          listenersRegistered: 0,
        };
      }

      const listenersRegistered = listeners.length;

      return listeners.map(listener => {
          const schema = listener.schema as ZodType<T>;
          const callback = listener.callback as Listener<ZodType<T>>;
          const result = schema.safeParse(payload);

          if (!result.success) {
            return {
              success: false,
              error: `Payload validation failed for [${eventName}]: ${result.error}`,
            };

          } else {
            try{
              //trigger callback that can be async,
              // without waiting for promise to resolve
              callback(payload);
              return {
                success: true,
                error: undefined,
              };
            }catch(e){
              return{
                success: false,
                error: e.message
              };
            }
          }
      }).reduce((acc: EmitStatus, curr) => {

          return {
            listenersRegistered: listenersRegistered,
            success: acc.success || curr.success,
            error: curr.error ? (curr.error ?? '') + '\n' + acc.error : acc.error,
            listenersExecuted: acc.listenersExecuted + (curr.success ? 1 : 0),
          };
      },{
        success: false,
        error: null,
        listenersExecuted: 0,
        listenersRegistered: listenersRegistered,
      });
  }

  /**
   * Register a listener for a specific event, with specific type
   * @returns Unsubscribe function
   * @param eventName
   * @param schema
   * @param callback
   */
  on<T extends ZodType<unknown>>(eventName: string, schema: T, callback: Listener<T>): Unsubscribe {
      let listeners = this.listeners[eventName];

      if (!listeners) {
        listeners = this.listeners[eventName] = [];
      }

      listeners.push({schema, callback});

      return () => {
          this.listeners[eventName] = listeners.filter(l => l.callback !== callback);
      };
  }

  /**
   * Registers a listener for the specified event that will be called only once.
   *
   * @param {string} eventName - The name of the event.
   * @param {T} schema - The Zod schema for the event payload.
   * @param {function} callback - The listener function to be called when the event is triggered.
   *   The callback function will receive the payload of the event.
   *
   * @return {function} - An unsubscribe function that can be used to manually remove the listener
   *   before it is executed.
   */
  once<T extends ZodType<unknown>>(eventName: string, schema: T, callback: Listener<T>): Unsubscribe {

      const off = this.on(eventName, schema, (payload: z.TypeOf<T>) => {
          callback(payload);
          off();
      });

      // Unsubscribe function is still provided to manually remove the listener before it is executed
      return off;
  }

}
